import _ from 'lodash';
import React, { useMemo } from 'react';

import CompositionContentType from 'models/composition/composition_content_type';
import connect from 'components/lib/connect';
import { getActiveConversationId } from 'actions/conversation/lib/conversation_helpers';
import { getAnswersList } from 'scripts/application/selectors/answers';

export const ANSWER_PANEL_DEFAULT_CONTEXT = {
  answersList: {
    answers: [],
    type: 'EMPTY',
  },
  canInsertAnswer: true,
  focusedAnswer: undefined,
  searchText: '',
  selectedAnswer: undefined,
  selectedAnswerType: undefined,
};
const AnswerPanelContext = React.createContext(ANSWER_PANEL_DEFAULT_CONTEXT);

export function AnswerPanelContextProviderBase({
  children,
  answerPanel,
  answersList,
  canInsertAnswer,
  languages,
  languagesImm,
  searchResultsImm,
  searchText,
  suggestionsImm,
}) {
  const value = useMemo(
    () => {
      const { searchLanguage, selectedAnswer, selectedAnswerLanguage, selectedAnswerType } = answerPanel;

      return {
        answersList,
        canInsertAnswer,
        focusedAnswer: answerPanel.history.length ? answerPanel.history[0].answer : selectedAnswer,
        history: answerPanel.history,
        languages,
        lastOpenedAt: answerPanel.lastOpenedAt,
        searchLanguage,
        searchText,
        selectedAnswer,
        selectedAnswerLanguage,
        selectedAnswerType,
        sortType: answerPanel.sortType,
      };
    },
    // Use the immutables rather than the arrays of searchResults & suggestions since those are _never_
    // the same between renders
    [answerPanel, canInsertAnswer, languagesImm, searchResultsImm, searchText, suggestionsImm]
  );

  return <AnswerPanelContext.Provider value={value}>{children}</AnswerPanelContext.Provider>;
}

const AnswerPanelWithoutCustomerStoresContextProvider = connect(mapStateWithoutCustomerStoresToProps)(
  AnswerPanelContextProviderBase
);
const AnswerPanelWithCustomerStoresContextProvider = connect(mapStateWithCustomerStoresToProps)(
  AnswerPanelWithoutCustomerStoresContextProvider
);
const AnswerPanelContextProvider = function({ isReadOnly, children }) {
  return isReadOnly ? (
    <AnswerPanelWithoutCustomerStoresContextProvider children={children} />
  ) : (
    <AnswerPanelWithCustomerStoresContextProvider children={children} />
  );
};
export { AnswerPanelContextProvider };

function mapStateWithCustomerStoresToProps({ getProvider, isFeatureEnabled }) {
  const currentLocation = getProvider('currentLocation').get();

  const conversationsProvider = getProvider('conversations');

  const compositionsProvider = getProvider('compositions');

  const composition = compositionsProvider.findBy({ id: currentLocation.currentCompositionId });
  const hasActiveConversation = !!getActiveConversationId(conversationsProvider);
  const canInsertAnswer =
    ((currentLocation.currentCompositionId && !hasActiveConversation) || hasActiveConversation) &&
    (!composition || composition.contentType() !== CompositionContentType.PHONE_CALL);

  return {
    canInsertAnswer,
  };
}

function mapStateWithoutCustomerStoresToProps({ getProvider, isFeatureEnabled }) {
  const answerPanelProvider = getProvider('answerPanel');
  const search = answerPanelProvider.get().searchResult;

  const currentLocation = getProvider('currentLocation').get();
  const suggestionsImm = getProvider('answerSuggestions')
    .immutableStore.binding.sub(currentLocation.currentCompositionId)
    .get();

  let languages = getProvider('languages').findAll();
  const languagesImm = getProvider('languages').immutableStore.binding.get();

  const answersList = getAnswersList({
    answerPanel: answerPanelProvider,
    currentLocation: getProvider('currentLocation'),
    answerSuggestions: getProvider('answerSuggestions'),
  });
  return {
    answerPanel: answerPanelProvider.get(),
    answersList,
    languages,
    languagesImm,
    searchText: _.get(search, 'query.text') || '',
    searchResultsImm: answerPanelProvider.store.binding.get('searchResult'),
    suggestionsImm,
  };
}

export default AnswerPanelContext;



// WEBPACK FOOTER //
// ./src/scripts/presentation/components/customer/answer_panel/answer_panel_context.jsx